import React, { useMemo, useRef, useState } from "react";
import Footer from "../components/footer";
import bg from "../assets/background.png";
import "../styles/contactPage.modules.css";

import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { usePages, useSchedule } from "../data/useLocations";
import Seo from "../components/Seo";

/** Pozycja doklejana na koniec listy zajęć z bazy. */
const FALLBACK_INTEREST = "Nie wiem jeszcze / potrzebuję pomocy z wyborem";

const EMPTY_FORM = {
  name: "",
  phone: "",
  email: "",
  message: "",
  consent: true,
  hp: "",
  interests: [],
  age: "",
  source: "",
  locationSlug: "all",
};

export default function Contact() {
  const { pages, loading: pagesLoading } = usePages();
  const { data } = useSchedule();

  /** Dyscypliny z systemu — ta sama lista co w sekcji "Poznaj zajęcia dla siebie". */
  const interestOptions = useMemo(() => {
    const names = (data?.disciplines || []).map((d) => d.name).filter(Boolean);
    return [...names, FALLBACK_INTEREST];
  }, [data]);

  const formOpenedAt = useRef(Date.now());

  const [form, setForm] = useState(EMPTY_FORM);
  const [sending, setSending] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");

  const onChange = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const onToggle = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.checked }));

  const toggleInterest = (val) => {
    setForm((p) => {
      const has = p.interests.includes(val);
      return {
        ...p,
        interests: has ? p.interests.filter((x) => x !== val) : [...p.interests, val],
      };
    });
  };

  const canSend = useMemo(() => {
    if (form.name.trim().length < 2) return false;
    if (!form.phone.trim() && !form.email.trim()) return false;
    if (!form.message.trim()) return false;
    if (!form.consent) return false;
    return true;
  }, [form]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setOk(false);

    if (form.hp) return;

    if (!canSend) {
      setErr("Uzupełnij imię + wiadomość oraz telefon lub email.");
      return;
    }

    try {
      setSending(true);

      const selected =
        form.locationSlug === "all"
          ? null
          : pages.find((p) => p.slug === form.locationSlug) ?? null;

      await httpsCallable(functions, "site_submitLead")({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
        consent: true,
        interests: form.interests,
        age: String(form.age || "").trim() || null,
        locationId: selected?.roomIds?.[0] ?? null,
        locationName: selected?.name ?? null,
        sourcePath: "/kontakt",
        source: form.source?.trim() || null,
        hp: form.hp,
        elapsedMs: Date.now() - formOpenedAt.current,
      });

      setOk(true);
      setForm(EMPTY_FORM);
      formOpenedAt.current = Date.now();
    } catch (e2) {
      console.error(e2);
      setErr(e2?.message || "Coś poszło nie tak. Spróbuj ponownie za chwilę.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        background: `#ededed url(${bg}) center / cover no-repeat`,
        paddingTop: "80px",
      }}
    >
      <Seo
        title="Kontakt"
        description="Zostaw kontakt, a wrócimy z ofertą zajęć dopasowaną do wieku i poziomu dziecka. Telefon 505 376 552, zapisy do wszystkich placówek."
        path="/kontakt"
      />

      <section
        style={{
          width: "92%",
          maxWidth: "1200px",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          margin: "10px auto",
        }}
      >
        <h1 style={{ margin: 0, fontSize: "34px", fontWeight: 900, color: "rgba(0,0,0,0.92)" }}>
          Kontakt
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: "13px",
            fontWeight: 700,
            color: "rgba(0,0,0,0.62)",
            maxWidth: "820px",
          }}
        >
          Zostaw nam kontakt, a wrócimy do Ciebie z ofertą zajęć dopasowaną pod Twoje preferencje!
        </p>
      </section>

      <div className="cp_wrap">
        <div className="cp_cards">
          <div className="cp_card">
            <div className="cp_cardTitle">Telefon</div>
            <div className="cp_cardValue">
              <a href="tel:+48505376552" style={{ color: "inherit", textDecoration: "none" }}>
                +48 505 376 552
              </a>
            </div>
            <div className="cp_cardHint">
              Zadzwoń lub napisz SMS, jeżeli nie odbierzemy, oddzwonimy do Ciebie.
            </div>
          </div>

          <div className="cp_card">
            <div className="cp_cardTitle">Email</div>
            <div className="cp_cardValue">
              <a
                href="mailto:wroclaw@szkola4stars.pl"
                style={{ color: "inherit", textDecoration: "none" }}
              >
                wroclaw@szkola4stars.pl
              </a>
            </div>
            <div className="cp_cardValue">
              <a
                href="mailto:jastrzebie@szkola4stars.pl"
                style={{ color: "inherit", textDecoration: "none" }}
              >
                jastrzebie@szkola4stars.pl
              </a>
            </div>
            <div className="cp_cardHint">W temacie lub treści wpisz lokalizację.</div>
          </div>

          <div className="cp_card">
            <div className="cp_cardTitle">Lokalizacje</div>
            <div className="cp_cardValue">
              {pagesLoading ? "-" : pages.length ? `${pages.length} placówek` : "-"}
            </div>
            <div className="cp_cardHint">Wybierz lokalizację w formularzu.</div>
          </div>
        </div>

        <div className="cp_formCard">
          <div className="cp_formHead">
            <h2 className="cp_h2">Formularz kontaktowy</h2>
            <p className="cp_sub">Wypełnij zgłoszenie - wrócimy do Ciebie z odpowiedzią!</p>
          </div>

          <form onSubmit={submit} className="cp_form" style={{ marginTop: "20px" }}>
            <input
              value={form.hp}
              onChange={onChange("hp")}
              className="cp_hp"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />

            <div className="cp_row">
              <div className="cp_field">
                <label className="cp_label" htmlFor="cp-name">
                  Imię i nazwisko
                </label>
                <input
                  id="cp-name"
                  value={form.name}
                  onChange={onChange("name")}
                  className="cp_input"
                  placeholder="Np. Anna Kowalska"
                  autoComplete="name"
                />
              </div>

              <div className="cp_field">
                <label className="cp_label" htmlFor="cp-age">
                  Wiek uczestnika
                </label>
                <input
                  id="cp-age"
                  value={form.age}
                  onChange={onChange("age")}
                  className="cp_input"
                  placeholder="Np. 8 lub 16+"
                  type="text"
                  inputMode="numeric"
                />
              </div>
            </div>

            <div className="cp_row">
              <div className="cp_field">
                <label className="cp_label" htmlFor="cp-phone">
                  Telefon
                </label>
                <input
                  id="cp-phone"
                  value={form.phone}
                  onChange={onChange("phone")}
                  className="cp_input"
                  placeholder="+48 ..."
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                />
              </div>

              <div className="cp_field">
                <label className="cp_label" htmlFor="cp-email">
                  Email (opcjonalnie)
                </label>
                <input
                  id="cp-email"
                  value={form.email}
                  onChange={onChange("email")}
                  className="cp_input"
                  placeholder="@"
                  type="email"
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="cp_field">
              <label className="cp_label" htmlFor="cp-location">
                Lokalizacja (opcjonalnie)
              </label>
              <select
                id="cp-location"
                value={form.locationSlug}
                onChange={onChange("locationSlug")}
                className="cp_select"
                disabled={pagesLoading}
              >
                <option value="all">
                  {pagesLoading ? "Wczytywanie..." : "Dowolna / jeszcze nie wiem"}
                </option>
                {pages.map((p) => (
                  <option key={p.slug} value={p.slug}>
                    {p.city && p.city !== p.name ? `${p.city} - ${p.name}` : p.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="cp_field">
              <span className="cp_label">
                Jakie zajęcia Cię interesują? (możesz zaznaczyć kilka)
              </span>

              <div className="cp_checks">
                {interestOptions.map((opt) => (
                  <label key={opt} className="cp_check">
                    <input
                      type="checkbox"
                      checked={form.interests.includes(opt)}
                      onChange={() => toggleInterest(opt)}
                    />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="cp_field">
              <label className="cp_label" htmlFor="cp-message">
                Wiadomość
              </label>
              <textarea
                id="cp-message"
                value={form.message}
                onChange={onChange("message")}
                className="cp_textarea"
                rows={5}
                placeholder="Np. chcę zapisać dziecko, kiedy są zajęcia, jaki poziom, itd."
                style={{ resize: "none" }}
              />
            </div>

            <div className="cp_field">
              <label className="cp_label" htmlFor="cp-source">
                Skąd się o nas dowiedziałaś/dowiedziałeś? (opcjonalnie)
              </label>
              <textarea
                id="cp-source"
                value={form.source}
                onChange={onChange("source")}
                className="cp_textarea"
                rows={2}
                placeholder="Np. szkołę polecił mi znajomy, facebook"
                style={{ resize: "none", height: "70px" }}
              />
            </div>

            <label className="cp_consent" style={{ marginBottom: "10px" }}>
              <input type="checkbox" checked={form.consent} onChange={onToggle("consent")} />
              Wyrażam zgodę na kontakt w sprawie zajęć.
            </label>

            {err ? (
              <div className="cp_err" role="alert">
                {err}
              </div>
            ) : null}
            {ok ? (
              <div className="cp_ok" role="status">
                Wysłane ✅ Odezwiemy się wkrótce.
              </div>
            ) : null}

            <button className="cp_submit" disabled={!canSend || sending} type="submit">
              {sending ? "Wysyłanie..." : "Wyślij wiadomość"}
            </button>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
}